var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { apiBaseUrl, WEBSOCKET_RECONNECT_MAX_TRY_COUNT, WEBSOCKET_1ST_RECONNECT_DELAY_MSEC, WEBSOCKET_RECONNECT_JITTER_MSEC, WEBSOCKET_RECONNECT_COUNTER_RESET_TIME_MSEC, } from '@crew/configs/constants';
import { IsIncomingMessage } from './models/common/incoming';
import { refreshAccessToken } from '../apiBase/apiBase';
// WebsocketのカスタムCloseCode
const WEBSOCKET_CLOSE_INVALID_ACCESS_TOKEN = 4000; // Backendと合わせる
/**
 * Websocket接続タイプ
 *  - application: 通常のアプリケーション用
 *  - callKeep: CallKeep用
 * 接続先はどちらも同じだが、callKeepの方は再接続時にsetTimeoutによる遅延を行わない
 */
export const WebsocketConnectType = {
    Application: 'application',
    CallKeep: 'callKeep',
};
/**
 * websocketの接続およびメッセージ受信を行う関数
 */
export const connectWebsocket = ({ onConnected, onReconnecting, onRecvMessage, onDisconnected, }, connectType) => {
    const wsUrl = new URL('api/v1/ws-connect', apiBaseUrl());
    // APIのベースURLのプロトコルに従ってwebsocket側も切り替える
    wsUrl.protocol = wsUrl.protocol === 'http:' ? 'ws:' : 'wss:';
    // 自動再接続で新たなインスタンスを作るので代入可にしておく
    let websocket;
    // 自動再接続するかどうか。明示的にcloseした場合にfalseにする
    let doReconnect = true;
    // 自動再接続でリトライした回数
    let reconnectTryCount = 0;
    /**
     * websocket接続開始する関数。自動再接続で再入するため関数として分離している
     * @returns
     */
    const connect = () => {
        console.info(`[WebsocketApi] Websocket opening... target: ${wsUrl}`);
        // react-native環境のWebSocketはURLオブジェクトを受け取ることができないため、
        // 明示的に文字列に変換する
        const ws = new WebSocket(wsUrl.toString());
        // 自動再接続時に使うタイマー
        let reconnectTimer;
        // 自動再接続リトライ回数をリセットするためのタイマー
        let reconnectResetTimer;
        // Websocket再接続用関数
        const reconnect = (e) => __awaiter(void 0, void 0, void 0, function* () {
            reconnectTimer = null;
            // 前回の接続終了コードがアクセストークンエラーだった場合、まずトークンのリフレッシュを行う
            if (e.code === WEBSOCKET_CLOSE_INVALID_ACCESS_TOKEN) {
                console.info('[WebsocketApi] Refreshing access token...');
                const refreshOk = yield refreshAccessToken();
                if (!refreshOk) {
                    console.error('[WebsocketApi] Refreshing access token failed.');
                    doReconnect = false;
                    onDisconnected === null || onDisconnected === void 0 ? void 0 : onDisconnected(true);
                    return;
                }
                else {
                    console.info('[WebsocketApi] Refreshing access token succeeded.');
                }
            }
            console.info('[WebsocketApi] Websocket reconnecting...');
            websocket = connect();
        });
        // メッセージ受信イベントハンドラ
        ws.onmessage = (e) => {
            const res = JSON.parse(e.data);
            if (IsIncomingMessage(res)) {
                console.info(`[WebsocketApi] Received message from ws. ${e.data}`);
                onRecvMessage(res);
            }
            else {
                console.warn(`[WebsocketApi] Invalid message from ws. ${e.data}`);
            }
        };
        // websocket接続完了ハンドラ
        ws.onopen = (e) => {
            console.info(`[WebsocketApi] Websocket opened to ${wsUrl}`);
            // 接続完了したのでリトライ回数をリセット
            // setTimeoutにより遅延させているのは、リフレッシュトークンの期限が切れている場合に即時リトライが行われて無限リトライになってしまうことを防ぐため
            // TODO: このsetTimeoutの遅延は、後のリファクタリングで不要となる見込み
            // https://break-tmc.atlassian.net/browse/CREW-15930
            reconnectResetTimer = window.setTimeout(() => {
                console.info(`[WebsocketApi] Reset reconnect counter.`);
                reconnectResetTimer = null;
                reconnectTryCount = 0;
            }, WEBSOCKET_RECONNECT_COUNTER_RESET_TIME_MSEC);
            onConnected();
        };
        // websocket接続終了ハンドラ
        ws.onclose = (e) => {
            console.info(`[WebsocketApi] Websocket closed to ${wsUrl}. code: ${e.code}, reason: ${e.reason}`, e);
            reconnectTimer && clearTimeout(reconnectTimer);
            reconnectTimer = null;
            reconnectResetTimer && clearTimeout(reconnectResetTimer);
            reconnectResetTimer = null;
            // 再接続する
            if (doReconnect) {
                reconnectTryCount++;
                if (reconnectTryCount > WEBSOCKET_RECONNECT_MAX_TRY_COUNT) {
                    console.error(`[WebsocketApi] Websocket reconnect failed.`);
                    doReconnect = false;
                    onDisconnected === null || onDisconnected === void 0 ? void 0 : onDisconnected(true);
                    return;
                }
                // 再接続までの遅延時間を2の指数で増加させ、そこにjitterを加える(exponential back-off and jitter)
                const exponentialBackOffedFactor = Math.pow(2, (reconnectTryCount - 1));
                const jitterFactor = (Math.random() - 0.5) * 2; // -1~1の乱数
                const reconnectWaitTime = WEBSOCKET_1ST_RECONNECT_DELAY_MSEC * exponentialBackOffedFactor +
                    WEBSOCKET_RECONNECT_JITTER_MSEC * jitterFactor;
                onReconnecting === null || onReconnecting === void 0 ? void 0 : onReconnecting(reconnectTryCount, reconnectWaitTime);
                // Websocket接続タイプに応じて再接続時にsetTimeoutするかどうかを振り分ける
                if (connectType === WebsocketConnectType.Application) {
                    console.info(`[WebsocketApi] Websocket try auto reconnecting. Wait for interval ${reconnectWaitTime} msec, connectType: ${connectType}, reconnectTryCount: ${reconnectTryCount}.`);
                    // Application用の接続は、再接続時にsetTimeoutで遅延させる
                    reconnectTimer = window.setTimeout(() => {
                        console.info(`[WebsocketApi] execute reconnect(), reconnectTryCount: ${reconnectTryCount}`);
                        reconnect(e);
                    }, reconnectWaitTime);
                }
                else if (connectType === WebsocketConnectType.CallKeep) {
                    console.info(`[WebsocketApi] Websocket try auto reconnecting. execute reconnect(), connectType: ${connectType}, reconnectTryCount: ${reconnectTryCount}.`);
                    // CallKeep用の接続は、再接続時にsetTimeoutで遅延させない
                    reconnect(e);
                }
            }
            else {
                console.info('[WebsocketApi] Websocket do not reconnect.');
                // 再接続しない、インスタンスを削除
                websocket = null;
                onDisconnected === null || onDisconnected === void 0 ? void 0 : onDisconnected(false);
            }
        };
        ws.onerror = (err) => {
            console.info('[WebsocketApi] Websocket error.', err);
        };
        return ws;
    };
    websocket = connect();
    return {
        close: () => {
            console.info('[WebsocketApi] connectWebsocket close() triggered.');
            doReconnect = false;
            websocket === null || websocket === void 0 ? void 0 : websocket.close();
        },
        send: (data) => {
            console.info('[WebsocketApi] connectWebsocket send() triggered. params:', data);
            if ((websocket === null || websocket === void 0 ? void 0 : websocket.readyState) !== WebSocket.OPEN) {
                console.info(`[WebsocketApi] connectWebsocket send() not executed. Websocket is not open. state: ${websocket === null || websocket === void 0 ? void 0 : websocket.readyState}`);
                return false;
            }
            websocket.send(data);
            return true;
        },
        isConnected: () => (websocket === null || websocket === void 0 ? void 0 : websocket.readyState) === WebSocket.OPEN,
    };
};
